import { useState, useEffect } from 'react';
import { Menu } from '../types/menu';
import api from '../services/api';

export interface UseMenuOptions {
  /** When set, refetches menu on this interval (ms). Polling updates are silent (no loading flash). */
  refetchIntervalMs?: number | null;
  /** When this changes, triggers an immediate silent refetch (e.g. SSE batch saved). */
  refetchNonce?: number;
}

export const useMenu = (menuId: string, options?: UseMenuOptions) => {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetchIntervalMs = options?.refetchIntervalMs ?? null;
  const refetchNonce = options?.refetchNonce ?? 0;

  // Initial load (and when menuId changes)
  useEffect(() => {
    let cancelled = false;

    const fetchMenu = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/menus/${menuId}`);
        if (!cancelled) {
          setMenu(response.data.menu);
          setError(null);
        }
      } catch (err) {
        console.error('Error fetching menu:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch menu');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMenu();

    return () => {
      cancelled = true;
    };
  }, [menuId]);

  // Background polling for live fast-upload updates
  useEffect(() => {
    if (!refetchIntervalMs || refetchIntervalMs <= 0) {
      return;
    }

    const fetchSilent = async () => {
      try {
        const response = await api.get(`/menus/${menuId}`);
        setMenu(response.data.menu);
        setError(null);
      } catch (err) {
        console.error('Error polling menu:', err);
      }
    };

    const intervalId = setInterval(fetchSilent, refetchIntervalMs);
    return () => clearInterval(intervalId);
  }, [menuId, refetchIntervalMs]);

  // One-off silent refetch when SSE reports a new translation batch
  useEffect(() => {
    if (refetchNonce <= 0) return;

    let cancelled = false;

    const fetchSilent = async () => {
      try {
        const response = await api.get(`/menus/${menuId}`);
        if (!cancelled) {
          setMenu(response.data.menu);
          setError(null);
        }
      } catch (err) {
        console.error('Error refetching menu:', err);
      }
    };

    fetchSilent();

    return () => {
      cancelled = true;
    };
  }, [menuId, refetchNonce]);

  return { menu, loading, error };
};
